import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Card, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AlertToast } from '../../components/AlertToast';
import { PageTitle } from '../../components/PageTitle';
import {
  type Announcement,
  type AnnouncementInput,
  useAnnouncements,
  useCreateAnnouncement,
  useDeleteAnnouncement,
  useUpdateAnnouncement,
} from '../../hooks/api/useAnnouncements';
import { useToast } from '../../hooks/useToast';
import {
  type AnnouncementAction,
  AnnouncementFormDialog,
} from './components/AnnouncementFormDialog';
import { AnnouncementsTable } from './components/AnnouncementsTable';

const SUCCESS_TOAST: Record<AnnouncementAction, string> = {
  saveDraft: 'announcements.toast.draftSaved',
  announce: 'announcements.toast.announced',
  save: 'announcements.toast.updated',
  retire: 'announcements.toast.retired',
};

/** Superadmin-only management of the "What's New" announcements every account sees. */
export const Announcements = () => {
  const { t } = useTranslation();
  const { data: announcements, isLoading, isError } = useAnnouncements();
  const { mutateAsync: createAnnouncement } = useCreateAnnouncement();
  const { mutateAsync: updateAnnouncement } = useUpdateAnnouncement();
  const { mutateAsync: deleteAnnouncement } = useDeleteAnnouncement();
  const toast = useToast(3000);
  const [toastMessage, setToastMessage] = useState('');
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Announcement | undefined>();
  const [deletedId, setDeletedId] = useState<string | null>(null);

  const liveAnnouncement = announcements?.find((a) => a.status === 'published');

  const showToast = (key: string) => {
    setToastMessage(t(key) as string);
    toast.trigger();
  };

  const openDialog = (announcement?: Announcement) => {
    setEditing(announcement);
    setDialogOpen(true);
  };

  const handleSubmit = async (
    action: AnnouncementAction,
    data: AnnouncementInput
  ) => {
    if (editing) {
      await updateAnnouncement({
        id: editing.id,
        data: action === 'announce' ? { ...data, status: 'published' } : data,
      });
    } else {
      await createAnnouncement({ data, publish: action === 'announce' });
    }
    setDialogOpen(false);
    showToast(SUCCESS_TOAST[action]);
  };

  const handleDelete = async (announcement: Announcement) => {
    await deleteAnnouncement(announcement.id);
    showToast('announcements.toast.deleted');
    setDeletedId(announcement.id);
  };

  // Once the refetched list no longer has the deleted row, its delete button (where
  // the closing dialog put focus back) is gone: move focus to the page heading.
  // Waiting for the list rather than a timer keeps this after the dialog's own
  // focus restore, whichever finishes first.
  useEffect(() => {
    if (!deletedId || announcements?.some((a) => a.id === deletedId)) return;
    setDeletedId(null);
    headingRef.current?.focus();
  }, [deletedId, announcements]);

  return (
    <>
      <PageTitle title={t('announcements.pageTitle') as string} />
      {toast.show && <AlertToast severity="success">{toastMessage}</AlertToast>}
      <Card
        variant="default"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 3,
        }}
      >
        <Box>
          <Typography
            component="h1"
            variant="h4"
            gutterBottom
            ref={headingRef}
            tabIndex={-1}
            sx={{ '&:focus': { outline: 'none' } }}
          >
            {t('announcements.pageTitle')}
          </Typography>
          <Typography variant="body1">
            {t('announcements.pageDescription')}
          </Typography>
        </Box>
        <Box sx={{ mt: { xs: 10, md: 0 }, flexShrink: 0 }}>
          <Button
            variant="primary"
            startIcon={<AddIcon aria-hidden="true" />}
            onClick={() => openDialog()}
          >
            {t('announcements.newAnnouncement')}
          </Button>
        </Box>
      </Card>

      {isLoading ? (
        <Typography role="status" sx={{ mt: 24 }}>
          {t('announcements.loading')}
        </Typography>
      ) : isError ? (
        <Typography role="alert" sx={{ mt: 24 }}>
          {t('announcements.loadError')}
        </Typography>
      ) : !announcements?.length ? (
        <Typography sx={{ mt: 24 }}>{t('announcements.empty')}</Typography>
      ) : (
        <AnnouncementsTable
          announcements={announcements}
          onOpen={openDialog}
          onDelete={handleDelete}
        />
      )}

      {dialogOpen && (
        <AnnouncementFormDialog
          key={editing?.id ?? 'new'}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          announcement={editing}
          liveAnnouncement={liveAnnouncement}
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
};
